import React, { useState, useEffect, useRef } from 'react';

/*
  ScheduleModal — opens from the 📍 Live map and 📋 Full visit buttons
  on the Team (Users) page.

  Props:
    empCode  string   e.g. "EMP015"
    empName  string   e.g. "Ashok"
    role     string   e.g. "sales_manager"
    view     'map' | 'log'   which tab to open first
    onClose  fn

  Calls:
    GET /api/v1/schedules/{empCode}/{date}          planned route + live position
    GET /api/v1/schedules/{empCode}/{date}/trail    GPS ping history for that day

  Date behaviour (shared across BOTH tabs):
    - selDate === today  -> live mode: trail-so-far + live dot + deviation alert,
                            auto-refresh every 15s (schedule) / 60s (trail)
    - selDate in past    -> history mode: full day trail with start/end markers,
                            no live dot, "Viewing history" banner, no polling
*/

const ACCENT = '#2563eb';
const STOP_COLOR = { completed: '#16a34a', current: '#2563eb', pending: '#9ca3af', missed: '#dc2626' };
const DEVIATION_THRESHOLD_M = 500; // meters off the planned route before flagging as deviated

// Tracker service — serves the visit-session events + typed route segments.
const TRACKER_URL = 'https://tracker.alokindia.co.in';

// ── Route styling: exactly THREE continuous solid line types ──
//   purple = planned route (origin -> stops in sequence)
//   blue   = actual travel  (every 'route' AND 'gap' segment, one unbroken line)
//   red    = off route      (persists)
// No dashes, no gray, no faint trails, no halos — anything else on the map is a
// marker, not a line.
const PLANNED_COLOR   = '#7C3AED';
const PLANNED_STYLE   = { color: PLANNED_COLOR, weight: 3, opacity: 0.9 };
const ROUTE_STYLE     = { color: ACCENT,        weight: 4, opacity: 0.9 };
const OFF_ROUTE_STYLE = { color: '#dc2626',     weight: 4, opacity: 0.9 };

const IDLE_COLOR = '#d97706'; // amber
const ORIGIN_COLOR = '#0F6E56';

// ── THE single UTC->IST bridge for everything coming from /api/track/* ──
// tracker_events timestamps are UTC-naive ISO strings ("2026-07-13T04:30:00").
// Two traps this closes:
//   1. no trailing 'Z' -> JS would parse them as *local* time (double-counting
//      the offset for an IST viewer, or silently wrong for any other viewer);
//   2. formatting in the browser's own zone -> wrong for anyone outside India.
// So: stamp as UTC, then format explicitly in Asia/Kolkata. No +5:30 arithmetic
// anywhere else in this file.
const toIST = ts => {
  if (!ts) return null;
  const s = /[Zz]|[+-]\d{2}:\d{2}$/.test(ts) ? ts : `${ts}Z`;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const fmtIST = ts => {
  const d = toIST(ts);
  return d
    ? d.toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })
    : '—';
};
const mins = n => (n == null ? '' : `${Math.round(n)}m`);
// minutes -> "7h 32m" / "48m"
const fmtDuration = n => {
  if (n == null) return '—';
  const t = Math.round(n);
  const h = Math.floor(t / 60);
  return h ? `${h}h ${t % 60}m` : `${t}m`;
};
// negative delta = early, positive = late
const deltaLabel = d => {
  if (d == null) return null;
  const r = Math.round(d);
  if (r === 0) return { text: 'on time', color: '#16a34a' };
  return r < 0
    ? { text: `early ${Math.abs(r)}m`, color: '#16a34a' }
    : { text: `late ${r}m`, color: '#dc2626' };
};
const minutesSince = ts => {
  const d = toIST(ts);
  return d ? Math.max(0, Math.round((Date.now() - d.getTime()) / 60000)) : 0;
};

function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = x => (x * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function closestPointOnSegment(p, a, b) {
  const [px, py] = p, [ax, ay] = a, [bx, by] = b;
  const dx = bx - ax, dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return a;
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return [ax + t * dx, ay + t * dy];
}

function nearestPointOnPath(point, path) {
  let best = null, bestDist = Infinity;
  for (let i = 0; i < path.length - 1; i++) {
    const cp = closestPointOnSegment(point, path[i], path[i + 1]);
    const d = haversineMeters(point, cp);
    if (d < bestDist) { bestDist = d; best = cp; }
  }
  return { point: best, distance: bestDist };
}

let leafletPromise = null;
function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(css);
    const js = document.createElement('script');
    js.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    js.onload = () => resolve(window.L);
    js.onerror = reject;
    document.body.appendChild(js);
  });
  return leafletPromise;
}

// (D) timeline row styling per event type
const EV_STYLE = {
  visit:       { color: '#16a34a',  icon: '📍', label: 'Visit' },
  travel_leg:  { color: ACCENT,     icon: '🚗', label: 'Travel' },
  idle_stop:   { color: IDLE_COLOR, icon: '⏸',  label: 'Idle stop' },
  off_route:   { color: '#dc2626',  icon: '⚠',  label: 'Off route' },
  gps_gap:     { color: '#9ca3af',  icon: '📡', label: 'GPS gap' },
  gps_gap_group: { color: '#9ca3af',  icon: '📡', label: 'GPS gaps' },
  not_reached: { color: '#9ca3af',  icon: '✕',  label: 'Not reached' },
  left_origin:     { color: ORIGIN_COLOR, icon: '🏠', label: 'Origin' },
  returned_origin: { color: ORIGIN_COLOR, icon: '🏠', label: 'Origin' },
};

// One-line description of an event for the Full visit timeline.
function describeEvent(e) {
  const m = e.meta || {};
  switch (e.event_type) {
    case 'visit': {
      const d = deltaLabel(m.delta_minutes);
      return {
        title: e.stop_name || 'Visit',
        detail: `${fmtIST(e.start_ts)} → ${e.end_ts ? fmtIST(e.end_ts) : 'now'} · ${mins(m.dwell_minutes)}`,
        tag: e.end_ts ? d : { text: 'on site', color: ACCENT },
      };
    }
    case 'travel_leg':
      return {
        title: 'Travel',
        detail: `${mins(m.moving_minutes)} moving · ${(m.distance_km ?? 0).toFixed(1)} km`
          + (m.avg_speed_kmh ? ` · ${m.avg_speed_kmh} km/h` : ''),
      };
    case 'idle_stop':
      return {
        title: m.nearest_account || 'Idle stop',
        detail: `${fmtIST(e.start_ts)} – ${fmtIST(e.end_ts)} · ${mins(m.duration_minutes)} stopped`,
      };
    case 'off_route':
      return {
        title: 'Off route',
        detail: `${fmtIST(e.start_ts)} – ${e.end_ts ? fmtIST(e.end_ts) : 'ongoing'}`
          + ` · max ${Math.round(m.max_deviation_m || 0)}m off · ${mins(m.duration_minutes)}`,
      };
    case 'gps_gap':
      return {
        title: 'GPS gap',
        detail: `${fmtIST(e.start_ts)} – ${fmtIST(e.end_ts)} · ${mins(m.minutes)} no signal`
          + (m.meters ? ` · ${(m.meters / 1000).toFixed(1)} km jump` : ''),
      };
    case 'gps_gap_group':
      // BUG 4: a run of consecutive gaps, collapsed into one row.
      return {
        title: `GPS gaps (${m.count})`,
        detail: `${fmtIST(e.start_ts)} – ${fmtIST(e.end_ts)} · signal lost ${m.count} times`
          + (m.max_jump_km != null ? ` · max jump ${m.max_jump_km.toFixed(1)} km` : ''),
      };
    case 'not_reached':
      return {
        title: e.stop_name || 'Stop',
        detail: (m.scheduled_time ? `scheduled ${m.scheduled_time}` : 'not scheduled')
          + (m.closest_approach_km != null ? ` · closest approach ${m.closest_approach_km} km` : ''),
        tag: { text: 'not reached', color: '#dc2626' },
      };
    case 'left_origin':
      return {
        title: `Left ${m.origin_name || 'origin'}`,
        detail: `Field day started · ${fmtIST(e.start_ts)}`,
      };
    case 'returned_origin':
      return {
        title: `Returned to ${m.origin_name || 'origin'}`,
        detail: `Field day ended · ${fmtIST(e.start_ts)}`,
      };
    default:
      return { title: e.event_type, detail: fmtIST(e.start_ts) };
  }
}

const initials = n => (n || '').split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase();
const fmtTime = iso => iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDateLabel = d => {
  try {
    const [y, m, dd] = d.split('-');
    return `${dd}-${m}-${y}`;
  } catch { return d; }
};

export default function ScheduleModal({ empCode, empName, role, view = 'map', onClose }) {
  const [tab, setTab] = useState(view);
  const [data, setData] = useState(null);
  const [trail, setTrail] = useState([]); // [{lat,lng,battery,is_moving,time_ist}]
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [deviation, setDeviation] = useState(null); // { meters, isDeviated } or null

  // ── visit-session events (tracker) ──
  const [session, setSession] = useState(null);   // { events, summary } | null
  const [segments, setSegments] = useState([]);   // [{type, points}]

  // ── stop edit/cancel (CRM proxy -> tracker) ──
  const [editingSeq, setEditingSeq] = useState(null);
  const [editForm, setEditForm] = useState({ company: '', address: '', time: '' });
  const [savingStop, setSavingStop] = useState(false);
  const [cancelReasonSeq, setCancelReasonSeq] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const mapRef = useRef(null);
  const mapObj = useRef(null);
  // (A) which (employee, date) we have already framed. fitBounds runs once per
  // key — never on a poll refresh, which is what used to reset the user's zoom.
  const fittedRef = useRef(null);

  const todayStr = new Date().toISOString().slice(0, 10);
  const [selDate, setSelDate] = useState(todayStr);
  const isToday = selDate === todayStr;

  // ---- schedule + live position (polls only when viewing today) ----
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const token = localStorage.getItem('access_token');
    const load = (first) => {
      fetch(`/api/v1/schedules/${empCode}/${selDate}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(raw => { if (alive) { setData(normalize(raw, empCode, selDate)); setErr(false); } })
        .catch(() => { if (alive && first) { setData(normalize(null, empCode, null)); setErr(false); } })
        .finally(() => { if (alive && first) setLoading(false); });
    };
    load(true);
    let iv = null;
    if (selDate === todayStr) iv = setInterval(() => load(false), 15000);
    return () => { alive = false; if (iv) clearInterval(iv); };
  }, [empCode, selDate]);

  // ---- GPS trail for the selected day (refreshes every 60s only for today) ----
  useEffect(() => {
    let alive = true;
    setTrail([]);
    const token = localStorage.getItem('access_token');
    const loadTrail = () => {
      fetch(`/api/v1/schedules/${empCode}/${selDate}/trail`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(j => { if (alive && j && Array.isArray(j.pings)) setTrail(j.pings); })
        .catch(() => {});
    };
    loadTrail();
    let iv = null;
    if (selDate === todayStr) iv = setInterval(loadTrail, 60000);
    return () => { alive = false; if (iv) clearInterval(iv); };
  }, [empCode, selDate]);

  // ---- visit-session events + typed route segments (tracker, polls every 20s) ----
  // Fail-soft: if the tracker is unreachable we simply keep session/segments empty
  // and the modal falls back to the raw trail + schedule it already had.
  useEffect(() => {
    let alive = true;
    setSession(null);
    setSegments([]);
    const q = `employee_code=${encodeURIComponent(empCode)}&date=${selDate}`;
    const loadSession = () => {
      fetch(`${TRACKER_URL}/api/track/session?${q}`)
        .then(r => { if (!r.ok) throw new Error('session'); return r.json(); })
        .then(j => { if (alive && j) setSession(j); })
        .catch(() => { if (alive) setSession(null); });
      fetch(`${TRACKER_URL}/api/track/route-segments?${q}`)
        .then(r => { if (!r.ok) throw new Error('segments'); return r.json(); })
        .then(j => { if (alive && j && Array.isArray(j.segments)) setSegments(j.segments); })
        .catch(() => { if (alive) setSegments([]); });
    };
    loadSession();
    let iv = null;
    if (selDate === todayStr) iv = setInterval(loadSession, 20000);
    return () => { alive = false; if (iv) clearInterval(iv); };
  }, [empCode, selDate]);

  const events = (session && session.events) || [];
  const summary = (session && session.summary) || null;
  const visitEvents = events.filter(e => e.event_type === 'visit');
  const idleEvents = events.filter(e => e.event_type === 'idle_stop');
  const notReachedEvents = events.filter(e => e.event_type === 'not_reached');
  const leftOrigin = events.find(e => e.event_type === 'left_origin') || null;
  const returnedOrigin = events.find(e => e.event_type === 'returned_origin') || null;


  // (4k) the field day is bookended by the origin events: leaving home/hotel
  // first, returning to it last. arrival/departure are folded into their visit.
  const timelineEvents = [
    ...(leftOrigin ? [leftOrigin] : []),
    ...events.filter(e => !['arrival', 'departure', 'left_origin', 'returned_origin']
      .includes(e.event_type)),
    ...(returnedOrigin ? [returnedOrigin] : []),
  ];

  // Match a scheduled stop to its tracker event by proximity — both ultimately
  // come from the same route_stops row, so <100 m is an exact match in practice.
  const eventForStop = (s, list) => list.find(e =>
    e.lat != null && s.lat != null &&
    haversineMeters([Number(e.lat), Number(e.lng)], [Number(s.lat), Number(s.lng)]) < 100
  );

  // Map the tracker's schedule shape -> what this modal renders.
  function normalize(raw, code, today) {
    const visits = (raw && (raw.visits || raw.stops)) || [];
    const stops = visits.map((v, i) => ({
      sequence:       v.sequence != null ? v.sequence : i + 1,
      client_name:    v.company || v.client_name || v.name || 'Stop',
      address:        v.address || '',
      lat:            v.lat != null ? Number(v.lat) : (v.latitude != null ? Number(v.latitude) : null),
      lng:            v.lng != null ? Number(v.lng) : (v.longitude != null ? Number(v.longitude) : null),
      status:         (function(st){
                        st = (st || 'pending').toLowerCase();
                        if (['visited','done','complete','completed'].includes(st)) return 'completed';
                        if (['active','enroute','en route','current','ongoing'].includes(st)) return 'current';
                        if (['missed','skipped','cancelled'].includes(st)) return 'missed';
                        return 'pending';
                      })(v.status),
      raw_status:     (v.status || '').toLowerCase(),
      scheduled_time: v.time || v.scheduled_time || null,
      check_in:       v.check_in || null,
      check_out:      v.check_out || null,
      duration_min:   v.duration_min != null ? v.duration_min : null,
      notes:          v.notes || '',
    }));
    return {
      person: { code, name: (raw && raw.sp_name) || null, role: null },
      date:   (raw && raw.date) || today,
      live:   (raw && raw.live) || null,
      start_point: (raw && raw.start_point) || null,
      stops,
    };
  }

  const authHeaders = () => {
    const token = localStorage.getItem('access_token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  const startEditStop = (s) => {
    setCancelReasonSeq(null);
    setEditingSeq(s.sequence);
    setEditForm({ company: s.client_name || '', address: s.address || '', time: s.scheduled_time || '' });
  };

  const saveEditStop = async (s) => {
    setSavingStop(true);
    try {
      const res = await fetch(`/api/v1/schedules/stop/${empCode}/${selDate}/${s.sequence}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          company: editForm.company || undefined,
          address: editForm.address || undefined,
          time: editForm.time || undefined,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setEditingSeq(null);
      setData(d => ({
        ...d,
        stops: d.stops.map(st => st.sequence === s.sequence
          ? { ...st, client_name: editForm.company, address: editForm.address, scheduled_time: editForm.time }
          : st),
      }));
    } catch (e) {
      alert('Could not save changes: ' + e.message);
    } finally {
      setSavingStop(false);
    }
  };

  const startCancelStop = (s) => {
    setEditingSeq(null);
    setCancelReasonSeq(s.sequence);
    setCancelReason('');
  };

  const doCancelStop = async (s) => {
    if (cancelReason.trim().length < 3) { alert('Please enter a reason (at least 3 characters).'); return; }
    setSavingStop(true);
    try {
      const res = await fetch(`/api/v1/schedules/stop/${empCode}/${selDate}/${s.sequence}/cancel`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ reason: cancelReason.trim() }),
      });
      if (!res.ok) throw new Error(await res.text());
      setCancelReasonSeq(null);
      setData(d => ({ ...d, stops: d.stops.map(st => st.sequence === s.sequence ? { ...st, status: 'missed' } : st) }));
    } catch (e) {
      alert('Could not cancel visit: ' + e.message);
    } finally {
      setSavingStop(false);
    }
  };

  const trailPts = trail
    .filter(p => p.lat != null && p.lng != null)
    .map(p => [Number(p.lat), Number(p.lng)]);
  const trailKm = (() => {
    let m = 0;
    for (let i = 1; i < trailPts.length; i++) m += haversineMeters(trailPts[i - 1], trailPts[i]);
    return m / 1000;
  })();

  // ---- Leaflet render ----
  useEffect(() => {
    if (tab !== 'map' || !data) return;
    let cancelled = false;
    loadLeaflet().then(L => {
      if (cancelled || !mapRef.current) return;

      // FIX: switching tabs unmounts the map div; a stale map instance bound to
      // the dead container made the map blank on return. Detect and rebuild.
      if (mapObj.current && mapObj.current.getContainer() !== mapRef.current) {
        mapObj.current.remove();
        mapObj.current = null;
      }
      if (!mapObj.current) {
        mapObj.current = L.map(mapRef.current, { zoomControl: true, attributionControl: false });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(mapObj.current);
        fittedRef.current = null;   // brand-new map instance -> frame it once
      }
      const map = mapObj.current;
      map.eachLayer(l => { if (!(l instanceof L.TileLayer)) map.removeLayer(l); });

      const stops = (data.stops || []).filter(s => s.lat != null && s.lng != null)
        .slice().sort((a, b) => a.sequence - b.sequence);
      const pts = [];

      // ---- 1. PLANNED ROUTE — solid purple, origin -> stop 1 -> stop 2 -> ...
      // Drawn FIRST so it sits UNDER the actual-travel lines. Always visible.
      const sp = data.start_point;
      const plannedPath = [];
      if (sp && sp.lat != null && sp.lng != null) plannedPath.push([sp.lat, sp.lng]);
      stops.forEach(s => plannedPath.push([s.lat, s.lng]));
      if (plannedPath.length > 1) {
        L.polyline(plannedPath, PLANNED_STYLE).addTo(map).bindPopup('Planned route');
        plannedPath.forEach(p => pts.push(p));
      }

      // ---- 2/3. ACTUAL TRAVEL — from /route-segments.
      // 'route' AND every 'gap' (whatever its size) draw as the same solid blue,
      // so the day reads as ONE continuous line. 'off_route' draws solid red.
      // Falls back to the raw trail if the tracker didn't answer.
      if (segments.length) {
        segments.forEach(seg => {
          const segPts = (seg.points || [])
            .filter(p => p && p[0] != null && p[1] != null)
            .map(p => [Number(p[0]), Number(p[1])]);
          if (segPts.length < 2) return;

          const isOff = seg.type === 'off_route';
          L.polyline(segPts, isOff ? OFF_ROUTE_STYLE : ROUTE_STYLE).addTo(map)
            .bindPopup(isOff ? '⚠ Off route' : 'On route');
          segPts.forEach(p => pts.push(p));
        });
      } else if (trailPts.length > 1) {
        L.polyline(trailPts, ROUTE_STYLE).addTo(map);
        trailPts.forEach(p => pts.push(p));
      }
      // (the green "S" day-start marker used to sit here — removed: the origin
      // marker and the live dot already say where the day began)
      if (!isToday && trailPts.length > 1) {
        const endIcon = L.divIcon({
          className: '',
          html: `<div style="width:16px;height:16px;border-radius:4px;background:#4b5563;
                 border:3px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.3);"></div>`,
          iconSize: [16, 16], iconAnchor: [8, 8],
        });
        L.marker(trailPts[trailPts.length - 1], { icon: endIcon }).addTo(map)
          .bindPopup(`<b>Day end</b><br>${trail[trail.length - 1].time_ist || ''}`);
      }

      // (planned route is drawn above, before the travel lines, so it sits under them)

      if (sp && sp.lat != null && sp.lng != null) {
        const startIcon = L.divIcon({
          className: '',
          html: `<div style="width:28px;height:28px;border-radius:50%;background:#fff;
                 border:3px solid #6b7280;box-shadow:0 1px 3px rgba(0,0,0,.3);
                 display:flex;align-items:center;justify-content:center;font-size:14px;">🏠</div>`,
          iconSize: [28, 28], iconAnchor: [14, 14],
        });
        // (4j) the origin is where he STAYS (home / hotel) — show the day's
        // bookends on it when we know them.
        const originName = sp.name
          || (leftOrigin && leftOrigin.meta && leftOrigin.meta.origin_name)
          || 'Origin';
        const bookends = [
          leftOrigin ? `Left ${fmtIST(leftOrigin.start_ts)}` : null,
          returnedOrigin ? `Returned ${fmtIST(returnedOrigin.start_ts)}` : null,
        ].filter(Boolean).join(' · ');

        const originMarker = L.marker([sp.lat, sp.lng], { icon: startIcon }).addTo(map)
          .bindPopup(`<b>🏠 ${originName}</b><br>Home / hotel — where he stays`
            + (bookends ? `<br>${bookends}` : '')
            + (leftOrigin && !returnedOrigin ? '<br><i>not back yet</i>' : ''));
        if (bookends) {
          originMarker.bindTooltip(bookends, {
            permanent: true, direction: 'right', offset: [14, 0], className: 'alok-stop-label',
          });
        }
      }

      // ---- (C) stop pins with inline labels, driven by the tracker events ----
      stops.forEach((s, i) => {
        const visit = eventForStop(s, visitEvents);
        const missed = !visit && eventForStop(s, notReachedEvents);
        const live = !!(visit && !visit.end_ts);          // on site right now

        let color = STOP_COLOR[s.status] || STOP_COLOR.pending;
        if (live) color = ACCENT;
        else if (visit) color = STOP_COLOR.completed;
        else if (missed) color = STOP_COLOR.pending;

        const html = missed
          ? `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);
               background:#fff;border:2px dashed ${STOP_COLOR.pending};box-shadow:0 1px 3px rgba(0,0,0,.25);
               display:flex;align-items:center;justify-content:center;">
               <span style="transform:rotate(45deg);color:${STOP_COLOR.pending};font-size:12px;font-weight:700;">${i + 1}</span></div>`
          : `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);
               background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.3);
               ${live ? 'animation:alokPulse 1.8s infinite;' : ''}
               display:flex;align-items:center;justify-content:center;">
               <span style="transform:rotate(45deg);color:#fff;font-size:12px;font-weight:700;">${i + 1}</span></div>`;

        const marker = L.marker([s.lat, s.lng], {
          icon: L.divIcon({ className: '', html, iconSize: [26, 26], iconAnchor: [13, 26] }),
        }).addTo(map).bindPopup(`<b>${s.client_name}</b><br>${s.address || ''}`);

        // inline label: "HH:MM → HH:MM · 42m · late 8m"  /  "on site 12m"
        let label = null;
        if (live) {
          label = `<b>on site ${minutesSince(visit.start_ts)}m</b>`;
        } else if (visit) {
          const d = deltaLabel(visit.meta && visit.meta.delta_minutes);
          label = `${fmtIST(visit.start_ts)} → ${fmtIST(visit.end_ts)} · ${mins(visit.meta && visit.meta.dwell_minutes)}`
            + (d ? ` · <span style="color:${d.color};font-weight:700">${d.text}</span>` : '');
        } else if (missed) {
          label = `<span style="color:${STOP_COLOR.pending}">not reached</span>`;
        }
        if (label) {
          marker.bindTooltip(label, {
            permanent: true, direction: 'right', offset: [10, -12], className: 'alok-stop-label',
          });
        }
        pts.push([s.lat, s.lng]);
      });

      // ---- (C) idle stops — amber pin + duration ----
      idleEvents.forEach(e => {
        if (e.lat == null || e.lng == null) return;
        const p = [Number(e.lat), Number(e.lng)];
        L.marker(p, {
          icon: L.divIcon({
            className: '',
            html: `<div style="width:20px;height:20px;border-radius:50%;background:${IDLE_COLOR};
                   border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.3);"></div>`,
            iconSize: [20, 20], iconAnchor: [10, 10],
          }),
        }).addTo(map)
          .bindTooltip(`idle ${mins(e.meta && e.meta.duration_minutes)}`, {
            permanent: true, direction: 'right', offset: [8, 0], className: 'alok-stop-label',
          })
          .bindPopup(`<b>Idle stop</b><br>${fmtIST(e.start_ts)} – ${fmtIST(e.end_ts)} · ${mins(e.meta && e.meta.duration_minutes)}`);
        pts.push(p);
      });

      // ---- live dot + deviation: ONLY when viewing today ----
      // The distance is still measured — it colours the live dot and the header
      // badge — but nothing is DRAWN for it: the map carries exactly three line
      // types (purple planned / blue actual / red off-route) and nothing else.
      let isDeviated = false;
      let deviationMeters = null;
      if (isToday && data.live && plannedPath.length > 1) {
        const nearest = nearestPointOnPath([data.live.lat, data.live.lng], plannedPath);
        deviationMeters = nearest.distance;
        isDeviated = nearest.distance > DEVIATION_THRESHOLD_M;
      }
      setDeviation(isToday && data.live ? { meters: deviationMeters, isDeviated } : null);

      if (isToday && data.live) {
        const liveColor = isDeviated ? '#dc2626' : ACCENT;
        const liveIcon = L.divIcon({
          className: '',
          html: `<div style="width:16px;height:16px;border-radius:50%;background:${liveColor};
                 border:3px solid #fff;box-shadow:0 0 0 4px ${isDeviated ? 'rgba(220,38,38,.3)' : 'rgba(37,99,235,.3)'};"></div>`,
          iconSize: [16, 16], iconAnchor: [8, 8],
        });
        L.marker([data.live.lat, data.live.lng], { icon: liveIcon }).addTo(map)
          .bindPopup(isDeviated ? `Off route (${Math.round(deviationMeters)}m)` : 'On route');
        pts.push([data.live.lat, data.live.lng]);
      }

      // ---- (A) ZOOM FIX ----
      // Frame the map ONCE per employee+date (and once per fresh map instance).
      // The poll refresh re-runs this effect to redraw polylines and markers, but
      // must NOT touch the viewport — calling fitBounds here unconditionally is
      // what used to yank the user's zoom/pan back every 15s.
      const fitKey = `${empCode}|${selDate}`;
      if (fittedRef.current !== fitKey) {
        const focus = [];
        trailPts.forEach(p => focus.push(p));
        if (isToday && data.live) focus.push([data.live.lat, data.live.lng]);
        plannedPath.forEach(p => focus.push(p));

        if (focus.length > 1) {
          map.fitBounds(focus, { padding: [50, 50], maxZoom: 14 });
          fittedRef.current = fitKey;
        } else if (focus.length === 1) {
          map.setView(focus[0], 13);
          fittedRef.current = fitKey;
        } else if (pts.length) {
          map.fitBounds(pts, { padding: [40, 40], maxZoom: 14 });
          fittedRef.current = fitKey;
        } else {
          // nothing to frame yet — show Mumbai but stay unlocked so we frame
          // properly once the trail/schedule actually arrives
          map.setView([19.076, 72.8777], 11);
        }
      }
      setTimeout(() => map.invalidateSize(), 50);
    });
    return () => { cancelled = true; };
  }, [tab, data, trail, selDate, empCode, segments, session]);

  useEffect(() => () => { if (mapObj.current) { mapObj.current.remove(); mapObj.current = null; } }, []);

  const stops = data ? (data.stops || []).slice().sort((a, b) => a.sequence - b.sequence) : [];
  const visibleStops = stops.filter(s => s.raw_status !== 'cancelled');
  const done = stops.filter(s => s.status === 'completed' || s.check_out).length;
  const name = empName || (data && data.person && data.person.name) || empCode;
  const empty = !loading && !err && stops.length === 0 && !(data && data.live && isToday) && trailPts.length === 0;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 860, height: '82vh', background: '#fff', borderRadius: 14, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,.3)', fontFamily: '-apple-system,system-ui,sans-serif' }}>
        <style>{`
          @keyframes alokPulse {
            0%   { box-shadow: 0 0 0 0 rgba(37,99,235,.55); }
            70%  { box-shadow: 0 0 0 12px rgba(37,99,235,0); }
            100% { box-shadow: 0 0 0 0 rgba(37,99,235,0); }
          }
          .alok-stop-label {
            background: rgba(255,255,255,.95);
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            box-shadow: 0 1px 3px rgba(0,0,0,.12);
            font-size: 11px;
            font-weight: 600;
            color: #374151;
            padding: 2px 6px;
          }
          .alok-stop-label::before { display: none; }
        `}</style>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid #eef0f4' }}>
          <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#7c4dff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600 }}>{initials(name)}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2430' }}>{name}</div>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>{empCode}{role ? ` · ${role}` : ''}</div>
          </div>
          {isToday && data && data.live && (
            <span style={{ fontSize: 12, color: deviation && deviation.isDeviated ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
              {deviation && deviation.isDeviated ? `⚠ Off route (${Math.round(deviation.meters)}m)` : '● Live'} · {fmtTime(data.live.last_ping)}{data.live.battery != null ? ` · 🔋${data.live.battery}%` : ''}
            </span>
          )}
          {!isToday && (
            <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, background: '#f3f4f6', padding: '3px 10px', borderRadius: 999 }}>
              🕘 Viewing history · {fmtDateLabel(selDate)}
            </span>
          )}
          <button onClick={onClose} aria-label="Close" style={{ marginLeft: 8, border: 'none', background: 'none', cursor: 'pointer', fontSize: 22, color: '#9ca3af', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 4, padding: '8px 14px 0', borderBottom: '1px solid #eef0f4' }}>
          {[['map', '📍 Live map'], ['log', '📋 Full visit']].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '8px 12px', fontSize: 13, fontWeight: 600, color: tab === k ? ACCENT : '#6b7280', borderBottom: tab === k ? `2px solid ${ACCENT}` : '2px solid transparent', marginBottom: -1 }}>{label}</button>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'hidden' }}>
          {loading && <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>}
          {err && <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Couldn't load schedule for {empCode}.</div>}
          {empty && (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
              No schedule or GPS history for {name} on {fmtDateLabel(selDate)}.
              <div style={{ marginTop: 12 }}>
                <input type="date" value={selDate} max={todayStr} onChange={e => setSelDate(e.target.value)}
                  style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px', fontSize: 13, color: '#374151' }} />
              </div>
            </div>
          )}

          {!loading && !err && !empty && tab === 'map' && (
            <div style={{ display: 'flex', height: '100%' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <div ref={mapRef} style={{ position: 'absolute', inset: 0, background: '#eaf0e8' }} />

                {/* legend — the three continuous solid line types */}
                <div style={{ position: 'absolute', bottom: 10, left: 10, zIndex: 800, background: 'rgba(255,255,255,.95)', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,.12)', padding: '7px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {[
                    [PLANNED_COLOR, 'Planned route'],
                    [ACCENT, 'Actual travel'],
                    ['#dc2626', 'Off route'],
                  ].map(([color, label]) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ width: 18, height: 3, borderRadius: 2, background: color, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</span>
                    </div>
                  ))}
                </div>
                {!isToday && (
                  <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 800, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,.12)', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
                    <span style={{ color: '#374151', fontWeight: 600 }}>🕘 History · {fmtDateLabel(selDate)}</span>
                    <button onClick={() => setSelDate(todayStr)} style={{ border: 'none', background: '#eff6ff', color: ACCENT, borderRadius: 6, padding: '3px 8px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Back to today</button>
                  </div>
                )}
              </div>
              <div style={{ width: 260, borderLeft: '1px solid #eef0f4', overflow: 'auto' }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid #f4f5f8' }}>
                  <div style={{ fontSize: 10, color: '#1a6fc4', marginBottom: 2, fontWeight: 700 }}>DATE — TAP TO CHANGE</div>
                  <input type="date" value={selDate} max={todayStr} onChange={e => setSelDate(e.target.value)}
                    style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 14, fontWeight: 600, color: '#111827', padding: 0, outline: 'none' }} />
                </div>
                <div style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #f4f5f8' }}>Route stops ({done}/{visibleStops.length})</div>
                {data && data.start_point && data.start_point.lat != null && (
                  <div style={{ display: 'flex', gap: 9, padding: '10px 14px', borderBottom: '1px solid #f4f5f8' }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#0F6E56', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>🏠</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2430' }}>{data.start_point.name || 'Starting point'}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>Route origin</div>
                    </div>
                  </div>
                )}
                {visibleStops.map((s, i) => {
                  const canModify = s.status !== 'completed' && s.status !== 'missed';
                  const isEditing = editingSeq === s.sequence;
                  const isCancelling = cancelReasonSeq === s.sequence;
                  const inputStyle = { fontSize: 12, padding: '4px 6px', border: '1px solid #e5e7eb', borderRadius: 6, width: '100%', boxSizing: 'border-box' };
                  return (
                    <div key={i} style={{ padding: '10px 14px', borderBottom: '1px solid #f4f5f8' }}>
                      <div style={{ display: 'flex', gap: 9 }}>
                        <div style={{ width: 20, height: 20, borderRadius: '50%', background: STOP_COLOR[s.status] || STOP_COLOR.pending, color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          {isEditing ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <input value={editForm.company} onChange={e => setEditForm(f => ({ ...f, company: e.target.value }))} placeholder="Company" style={inputStyle} />
                              <input value={editForm.address} onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))} placeholder="Address" style={inputStyle} />
                              <input value={editForm.time} onChange={e => setEditForm(f => ({ ...f, time: e.target.value }))} placeholder="HH:MM" style={inputStyle} />
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button disabled={savingStop} onClick={() => saveEditStop(s)} style={{ fontSize: 11, fontWeight: 600, color: '#fff', background: ACCENT, border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>{savingStop ? 'Saving…' : 'Save'}</button>
                                <button disabled={savingStop} onClick={() => setEditingSeq(null)} style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>Cancel</button>
                              </div>
                            </div>
                          ) : isCancelling ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2430' }}>{s.client_name}</div>
                              <input value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Reason for cancelling (required)" style={{ ...inputStyle, border: '1px solid #fecaca' }} />
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button disabled={savingStop} onClick={() => doCancelStop(s)} style={{ fontSize: 11, fontWeight: 600, color: '#fff', background: '#dc2626', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>{savingStop ? 'Cancelling…' : 'Confirm cancel'}</button>
                                <button disabled={savingStop} onClick={() => setCancelReasonSeq(null)} style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>Back</button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2430' }}>{s.client_name}</div>
                                {canModify && (
                                  <span style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                                    <button onClick={() => startEditStop(s)} title="Edit stop" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: '#9ca3af', padding: 2 }}>✏️</button>
                                    <button onClick={() => startCancelStop(s)} title="Cancel stop" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: '#9ca3af', padding: 2 }}>🚫</button>
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 11, color: '#9ca3af' }}>{s.check_in ? `In ${fmtTime(s.check_in)}` : (s.scheduled_time || 'scheduled')}{s.check_out ? ` · Out ${fmtTime(s.check_out)}` : ''}</div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {trailPts.length > 0 && (
                  <div style={{ padding: '10px 14px', fontSize: 11, color: '#6b7280', borderBottom: '1px solid #f4f5f8' }}>
                    📡 {trailPts.length} pings · {trailKm.toFixed(1)} km travelled
                  </div>
                )}
                <button onClick={() => setTab('log')} style={{ margin: 12, width: 'calc(100% - 24px)', padding: 8, border: '1px solid #bbf7d0', background: '#ecfdf5', color: '#16a34a', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Open full visit →</button>
              </div>
            </div>
          )}

          {!loading && !err && !empty && tab === 'log' && (
            <div style={{ height: '100%', overflow: 'auto', padding: 18 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(110px,1fr))', gap: 10, marginBottom: 18 }}>
                {/* Visits = distinct scheduled stops the SP reached (150m/5min, from visit_logs) */}
                <Metric label="Visits" value={summary ? `${summary.visits_logged}/${summary.stops_scheduled}` : `${done}/${stops.length}`} />
                <Metric label="Status" value={isToday ? (data && data.live ? 'active' : 'planned') : 'history'} />
                <Metric label="Total stops" value={`${stops.length}`} />
                <div style={{ background: '#f9fafb', borderRadius: 8, padding: 10, border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: 10, color: '#1a6fc4', marginBottom: 2 }}>Date — tap to change</div>
                  <input type="date" value={selDate} max={todayStr} onChange={e => setSelDate(e.target.value)}
                    style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 14, fontWeight: 600, color: '#111827', padding: 0, outline: 'none' }} />
                  {!isToday && <div style={{ fontSize: 9, color: '#854F0B', marginTop: 2 }}>Past date — map shows this day's trail</div>}
                </div>
              </div>
              {/* (D) summary strip — visit / travel / idle minutes */}
              {summary && (
                <div style={{ display: 'flex', gap: 0, marginBottom: 16, border: '1px solid #eef0f4', borderRadius: 10, overflow: 'hidden' }}>
                  {[
                    // BUG 3: real active span (last - first ping); "—" only with zero pings.
                    ['Field day',
                      summary.day_span_minutes != null ? fmtDuration(summary.day_span_minutes) : '—',
                      ORIGIN_COLOR],
                    ['On site', mins(summary.visit_minutes), '#16a34a'],
                    ['Travelling', mins(summary.travel_minutes), ACCENT],
                    ['Idle', mins(summary.idle_minutes), IDLE_COLOR],
                    ['Distance', `${(summary.km ?? 0).toFixed(1)} km`, '#6b7280'],
                    ['Stops', `${summary.stops_visited}/${summary.stops_total}`, '#6b7280'],
                  ].map(([label, value, color], i) => (
                    <div key={label} style={{ flex: 1, padding: '10px 12px', borderLeft: i ? '1px solid #eef0f4' : 'none' }}>
                      <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* (D) event timeline — visits, travel legs, idle stops, off-route, gaps, not-reached */}
              {timelineEvents.length > 0 && timelineEvents
                .map((e, i, arr) => {
                  const st = EV_STYLE[e.event_type] || EV_STYLE.gps_gap;
                  const { title, detail, tag } = describeEvent(e);
                  const live = e.event_type === 'visit' && !e.end_ts;
                  return (
                    <div key={`${e.event_type}-${e.start_ts}-${i}`} style={{ display: 'grid', gridTemplateColumns: '54px 22px 1fr', gap: 8, position: 'relative', paddingBottom: 14 }}>
                      {i < arr.length - 1 && <span style={{ position: 'absolute', left: 64, top: 20, bottom: -2, width: 1, background: '#eef0f4' }} />}
                      <span style={{ fontSize: 11, color: '#6b7280', textAlign: 'right', paddingTop: 2 }}>{fmtIST(e.start_ts)}</span>
                      <span style={{ display: 'flex', justifyContent: 'center' }}>
                        <span style={{
                          width: 14, height: 14, borderRadius: '50%', marginTop: 2,
                          background: live ? '#fff' : st.color,
                          border: live ? `3px solid ${st.color}` : '2px solid #fff',
                          boxShadow: `0 0 0 1px ${st.color}`,
                          animation: live ? 'alokPulse 1.8s infinite' : 'none',
                        }} />
                      </span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2430', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span>{st.icon} {title}</span>
                          <span style={{ fontSize: 10, fontWeight: 600, color: st.color, background: `${st.color}18`, borderRadius: 999, padding: '1px 7px' }}>{st.label}</span>
                          {tag && <span style={{ fontSize: 11, fontWeight: 700, color: tag.color }}>{tag.text}</span>}
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{detail}</div>
                      </div>
                    </div>
                  );
                })}

              {/* fallback: the schedule-only timeline, when the tracker returned no session */}
              {events.length === 0 && (<>
              {data && data.start_point && data.start_point.lat != null && (
                <div style={{ display: 'grid', gridTemplateColumns: '54px 22px 1fr', gap: 8, position: 'relative', paddingBottom: 14 }}>
                  <span style={{ position: 'absolute', left: 64, top: 20, bottom: -2, width: 1, background: '#eef0f4' }} />
                  <span style={{ fontSize: 11, color: '#6b7280', textAlign: 'right', paddingTop: 2 }}>start</span>
                  <span style={{ display: 'flex', justifyContent: 'center' }}><span style={{ width: 14, height: 14, borderRadius: '50%', background: '#0F6E56', border: '2px solid #fff', boxShadow: '0 0 0 1px #0F6E56', marginTop: 2 }} /></span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2430' }}>🏠 {data.start_point.name || 'Starting point'}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>Route origin</div>
                  </div>
                </div>
              )}
              {stops.map((s, i) => {
                const c = STOP_COLOR[s.status] || STOP_COLOR.pending;
                return (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '54px 22px 1fr', gap: 8, position: 'relative', paddingBottom: 14 }}>
                    {i < stops.length - 1 && <span style={{ position: 'absolute', left: 64, top: 20, bottom: -2, width: 1, background: '#eef0f4' }} />}
                    <span style={{ fontSize: 11, color: '#6b7280', textAlign: 'right', paddingTop: 2 }}>{s.check_in ? fmtTime(s.check_in) : (s.scheduled_time || '')}</span>
                    <span style={{ display: 'flex', justifyContent: 'center' }}><span style={{ width: 14, height: 14, borderRadius: '50%', background: c, border: '2px solid #fff', boxShadow: '0 0 0 1px ' + c, marginTop: 2 }} /></span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2430' }}>{s.client_name}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{s.address || ''}{s.check_out ? ` · out ${fmtTime(s.check_out)}` : ''}{s.duration_min != null ? ` · ${s.duration_min} min` : ''} · <span style={{ color: c, fontWeight: 600 }}>{s.status}</span></div>
                      {s.notes && <div style={{ marginTop: 5, background: '#f9fafb', border: '1px solid #f4f5f8', borderRadius: 8, padding: '7px 10px', fontSize: 12, color: '#374151' }}>📝 {s.notes}</div>}
                    </div>
                  </div>
                );
              })}
              {stops.length === 0 && trailPts.length > 0 && (
                <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                  No scheduled visits on this day — but GPS shows {trailPts.length} pings, {trailKm.toFixed(1)} km travelled. Check the Live map tab for the trail.
                </div>
              )}
              </>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 11, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#1f2430' }}>{value}</div>
    </div>
  );
}
